import { React, classNames } from 'libs/react';
import { useModal } from 'components/modal/ModalStack';
import { Button } from 'components/Button';
import { ButtonGroup } from 'components/ButtonGroup';
import { Text } from 'components/Text';
import { Icon } from 'components/Icon';
import { toMoney } from 'components/Money';
import { formatCardLabel, formatCheckedAgo, paymentStripeInfo, stripePaymentState } from './stripePaymentInfo';
import './DealPaymentDetails.scss';
const STATE_PICTO = {
    AWAITING_PAYMENT: 'clock',
    CHECK_ERROR: 'circle-exclamation',
    COMPLETED: 'circle-check',
    NOT_STARTED: 'circle-minus',
    PAID_PENDING: 'triangle-exclamation',
    SESSION_EXPIRED: 'circle-exclamation'
};
function stateLabel(state) {
    switch (state) {
        case 'COMPLETED':
            return t('Paid on Stripe');
        case 'PAID_PENDING':
            return t('Paid on Stripe — completion pending');
        case 'CHECK_ERROR':
            return t('Stripe verification failed');
        case 'SESSION_EXPIRED':
            return t('Stripe checkout expired');
        case 'AWAITING_PAYMENT':
            return t('Awaiting customer payment');
        default:
            return t('No Stripe payment initiated');
    }
}
/**
 * Content presented in the modal stack (`style: 'dialog'`). Shows what Stripe
 * actually knows about a payment, so a sale stuck at "pending payment" can be
 * told apart from one the customer never paid.
 *
 * @component DealPaymentDetails
 * @since 1.0.0
 */
export function DealPaymentDetails(props) {
    var _a, _b, _c, _d, _e;
    const { payment } = props;
    const modal = useModal();
    const info = paymentStripeInfo(payment.infos);
    const state = stripePaymentState(payment.status, info);
    const isCompleted = state === 'COMPLETED';
    const rows = [
        { label: t('Description'), value: (_a = payment.description) !== null && _a !== void 0 ? _a : undefined },
        { label: t('Amount'), value: toMoney(payment.amount) },
        { label: t('Payment method'), value: formatCardLabel(info.card) },
        { label: t('Link'), value: info.linkEmail },
        {
            label: t('Checkout'),
            value: isCompleted ? undefined : (_b = info.check) === null || _b === void 0 ? void 0 : _b.sessionStatus
        },
        {
            label: t('Payment'),
            value: isCompleted ? undefined : (_c = info.check) === null || _c === void 0 ? void 0 : _c.paymentStatus
        },
        { label: t('Last check'), value: formatCheckedAgo((_d = info.check) === null || _d === void 0 ? void 0 : _d.checkedAt) },
        { label: t('Payment intent'), mono: true, value: info.paymentIntentId },
        { label: t('Checkout session'), mono: true, value: info.sessionId }
    ];
    const stateClassName = classNames('deal-payment-details-state', `deal-payment-details-state--${state.toLowerCase().replace(/_/g, '-')}`);
    return (React.createElement("div", { className: "deal-payment-details" },
        React.createElement("div", { className: "deal-payment-details-body" },
            React.createElement("div", { className: stateClassName },
                React.createElement(Icon, { className: "deal-payment-details-state-picto", value: STATE_PICTO[state] }),
                React.createElement("div", { className: "deal-payment-details-state-content" },
                    React.createElement(Text, { className: "deal-payment-details-state-label", value: stateLabel(state) }),
                    state === 'PAID_PENDING' && (React.createElement(Text, { className: "deal-payment-details-state-infos", value: t('Stripe captured this payment but the sale is still pending. It is completed automatically within 15 minutes.') })),
                    state === 'CHECK_ERROR' && ((_e = info.check) === null || _e === void 0 ? void 0 : _e.error) && (React.createElement(Text, { className: "deal-payment-details-state-infos deal-payment-details-state-infos--error", value: info.check.error })))),
            React.createElement("div", { className: "deal-payment-details-list" }, rows
                .filter((row) => row.value)
                .map((row) => (React.createElement("div", { className: "deal-payment-details-row", key: row.label },
                React.createElement(Text, { className: "deal-payment-details-row-label", value: row.label }),
                React.createElement(Text, { className: classNames('deal-payment-details-row-value', {
                        'deal-payment-details-row-value--mono': row.mono
                    }), value: row.value })))))),
        React.createElement("div", { className: "deal-payment-details-foot" },
            React.createElement(ButtonGroup, null,
                React.createElement(Button, { size: "small", style: "primary", label: t('Close'), onPress: () => modal.dismiss() })))));
}
